import { useLayoutEffect, useRef, useState } from 'react';

/** Keep the app's screen baseline stable. Only floating UI follows the keyboard. */
export function useHomeViewport(enabled: boolean) {
  const baseline = useRef(window.innerHeight);
  const width = useRef(window.innerWidth);
  const [viewport, setViewport] = useState(() => ({
    height: window.innerHeight,
    visibleHeight: window.visualViewport?.height ?? window.innerHeight,
    bottomInset: 0,
    keyboardOpen: false,
  }));
  useLayoutEffect(() => {
    if (!enabled) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const vv = window.visualViewport;
        if (vv && Math.abs(vv.scale - 1) > .01) return;
        const visibleHeight = vv?.height ?? window.innerHeight;
        if (Math.abs(window.innerWidth - width.current) > 80) {
          baseline.current = window.innerHeight;
          width.current = window.innerWidth;
        } else {
          baseline.current = Math.max(baseline.current, window.innerHeight);
        }
        const bottomInset = Math.max(0, baseline.current - visibleHeight - (vv?.offsetTop ?? 0));
        const next = { height: baseline.current, visibleHeight, bottomInset: bottomInset > 120 ? bottomInset : 0, keyboardOpen: bottomInset > 120 };
        setViewport(previous =>
          Math.abs(previous.height - next.height) < 1 && Math.abs(previous.bottomInset - next.bottomInset) < 1 && Math.abs(previous.visibleHeight - visibleHeight) < 1
            ? previous : next);
      });
    };
    measure();
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    document.addEventListener('focusin', measure);
    document.addEventListener('focusout', measure);
    return () => {
      cancelAnimationFrame(frame);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      document.removeEventListener('focusin', measure);
      document.removeEventListener('focusout', measure);
    };
  }, [enabled]);
  return viewport;
}
